import React from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';

import { EngagementModernizeModal } from '@confluence/modernize-confluence-ipm-modal/entry-points/EngagementModernizeModal.tsx';

import {
	useNav4GlobalSegment,
	useHasNotSeenGlobalNav4Changeboarding,
} from '../segments/nav4/useNav4GlobalSegment.tsx';
import { useNav4SpaceSegment } from '../segments/nav4/useNav4SpaceSegment.tsx';
import { useLiveDocsSegment } from '../segments/live-docs/useLiveDocsSegment.tsx';
import { useLiveDocModal } from '../segments/live-docs/useLiveDocModal.ts';
import { LiveDocModal } from '../segments/live-docs/LiveDocModal.tsx';
import { useContentWrapperSegment } from '../segments/content-wrapper/useContentWrapperSegment.tsx';

import {
	type STEPS,
	useSetModernizeChangeboardingOnboardingState,
} from './useModernizeChangeboardingOnboardingState.ts';
import { useEligibleForModernizeModal } from './useEligibleForModernizeModal.tsx';

export type TourStepComponent = React.FC<{
	messageType?: 'transactional' | 'engagement' | 'marketing';
	onNext: () => void;
	onBack: () => void;
	onDismiss: () => void;
	progress: {
		step: number;
		steps: number;
		isFirst: boolean;
		isLast: boolean;
	};
}>;

export type Segment = {
	stepsToComplete?: STEPS[];
	modal?: React.ReactNode | null;
	steps: TourStepComponent[];
	onComplete: () => void;
	loading?: boolean;
	error?: ApolloErrorInterop | Error;
};

export type SegmentHook = () => Segment;

export const useCombinedSegments = () => {
	const nav4GlobalSegment = useNav4GlobalSegment();
	const nav4SpaceSegment = useNav4SpaceSegment();
	let liveDocsSegment = useLiveDocsSegment();
	const liveDocModalState = useLiveDocModal();
	const contentWrapperSegment = useContentWrapperSegment();
	const { completeSegments } = useSetModernizeChangeboardingOnboardingState();
	const modernizeModalState = useEligibleForModernizeModal();
	const { hasNotSeenGlobalNav4Changeboarding } = useHasNotSeenGlobalNav4Changeboarding();
	const loading =
		nav4GlobalSegment.loading ||
		nav4SpaceSegment.loading ||
		liveDocsSegment.loading ||
		liveDocModalState.loading ||
		contentWrapperSegment.loading ||
		modernizeModalState.loading;
	const error =
		nav4GlobalSegment.error ||
		nav4SpaceSegment.error ||
		liveDocsSegment.error ||
		liveDocModalState.error ||
		contentWrapperSegment.error ||
		modernizeModalState.error;

	let modal = null;

	if (loading) {
		return {
			modal,
			loading: true,
			steps: [],
			onComplete: () => {},
		};
	}
	if (error) {
		return {
			modal,
			error,
			steps: [],
			onComplete: () => {},
		};
	}

	if (hasNotSeenGlobalNav4Changeboarding && liveDocsSegment.steps.length > 0) {
		// not seen global nav 4, then don't show live docs segment yet, wait til next eligible time.
		liveDocsSegment = {
			steps: [],
			loading: false,
			onComplete: () => {},
		};
	}

	if (liveDocModalState.isEligibleToSeeModal && liveDocsSegment.steps.length > 0) {
		modal = <LiveDocModal />;
	}

	const steps = [];
	if (hasNotSeenGlobalNav4Changeboarding) {
		// not seen global nav 4
		steps.push(
			...nav4GlobalSegment.steps,
			...nav4SpaceSegment.steps,
			...contentWrapperSegment.steps,
		);
	} else {
		// seen global nav 4
		steps.push(...liveDocsSegment.steps, ...contentWrapperSegment.steps, ...nav4SpaceSegment.steps);
	}

	if (!modal && steps.length === 0 && modernizeModalState.eligible) {
		modal = (
			<EngagementModernizeModal
				source="modernize-ga-changeboarding"
				onClose={modernizeModalState.onDismiss}
				onInteraction={modernizeModalState.onInteraction}
			/>
		);
	}

	return {
		modal,
		steps,
		onComplete: () => {
			const onboardingStateKeysToComplete: STEPS[] = [];

			[nav4GlobalSegment, nav4SpaceSegment, liveDocsSegment, contentWrapperSegment].forEach(
				({ steps, stepsToComplete }) => {
					if (steps.length > 0) {
						onboardingStateKeysToComplete.push(...(stepsToComplete ?? []));
					}
				},
			);

			if (onboardingStateKeysToComplete.length > 0) {
				completeSegments(onboardingStateKeysToComplete);
			}
		},
	};
};
